import React, { lazy, Suspense } from 'react';
import ProtectedRoute from '../../shared/components/ProtectedRoute';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

// Lazy load settings components
const Settings = lazy(() => import('../../routes/settings/index'));
const AnimalGrades = lazy(() => import('../../routes/settings/animal-grades/list'));
const AnimalTypes = lazy(() => import('../../routes/settings/animal-types/list'));
const Attributes = lazy(() => import('../../routes/settings/attributes/list'));
const AttributeCategories = lazy(() => import('../../routes/settings/attribute-categories/list'));
const DataTypes = lazy(() => import('../../routes/settings/data-types/list'));
const CustomersSettings = lazy(() => import('../../routes/settings/customers/list'));
const PorkDefectTypes = lazy(() => import('../../routes/settings/pork-defect-types/list'));
const PriceAttributes = lazy(() => import('../../routes/settings/price-attributes/list'));
const PriceAttributeEdit = lazy(() => import('../../routes/settings/price-attributes/edit'));
const BeefProducers = lazy(() => import('../../routes/settings/producers/beef-list'));
const PorkProducers = lazy(() => import('../../routes/settings/producers/pork-list'));
const ProducerContacts = lazy(() => import('../../routes/settings/producers/contacts'));
const Haulers = lazy(() => import('../../routes/settings/haulers/list'));
const LivestockCenters = lazy(() => import('../../routes/settings/livestock-centers/list'));
const SlaughterPlants = lazy(() => import('../../routes/settings/slaughter-plants/list'));
const SystemSettingsPage = lazy(() => import('../../routes/settings/system-settings/list'));
const SystemSettingEdit = lazy(() => import('../../routes/settings/system-settings/edit'));
const PorkPayGrid = lazy(() => import('../../routes/settings/pork-pay-grid/list'));
const PorkPayGridDetail = lazy(() => import('../../routes/settings/pork-pay-grid/detail'));

/**
 * Settings feature routes with lazy loading
 * All settings routes require authentication
 */
export const settingsRoutes = [
  {
    path: '/settings',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <Settings />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/animalgrades',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <AnimalGrades />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/animaltypes',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <AnimalTypes />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/attributes',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <Attributes />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/attribute-categories',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <AttributeCategories />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/data-types',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <DataTypes />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/customers',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <CustomersSettings />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/pork-defect-types',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PorkDefectTypes />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/price-attributes',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PriceAttributes />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/price-attributes/edit',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PriceAttributeEdit />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/producers/beef-list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <BeefProducers />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/producers/pork-list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PorkProducers />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/producers/:animalType/:producerId/contacts',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <ProducerContacts />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/haulers/list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <Haulers />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/livestock-centers/list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <LivestockCenters />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/slaughter-plants/list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <SlaughterPlants />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/system-settings/list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <SystemSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/system-settings/edit',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <SystemSettingEdit />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/pork-pay-grids/list',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PorkPayGrid />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/pork-pay-grids/:headerId',
    element: (
      <ProtectedRoute requiredRoles={['LMS.Admin']}>
        <Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
          <PorkPayGridDetail />
        </Suspense>
      </ProtectedRoute>
    ),
  },
];

export default settingsRoutes;
